import React from "react";
import ReactDOM from "react-dom/client";
import { Suspense } from "react";
import { HashRouter } from "react-router-dom";
import { Toaster } from "sonner";
import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";

import "@fontsource-variable/geologica";
import "../boundary/__styles/globals.css";

import 'swiper/css';
import 'swiper/css/pagination';

import { AppRouter } from "@arch/routers/router";
import { DebugProvider } from "@boundary/components/debugWidget";
import { OrderFeedbackManager } from "@boundary/components/orderFeedbackManager";
import { CloseCellModalManager } from "@boundary/components/closeCellModalManager";
import { preloadYandexMaps } from "@boundary/_hooks/useYandexMaps";
import { initDevTools } from "@arch/boot/devTools";
import { calculateOffset } from "@shared/integrations/telegram/safeArea";
import { getStartParam } from "@shared/integrations/telegram/init";
import { preloadBackgroundImages } from "@shared/utils/imagePreloader";
import { markEntry, trackFunnel } from "@shared/utils/analytics";
import { applyDemoLoginFromUrl, hasDemoTicket } from "@shared/integrations/demoLogin";

// Предзагружаем Yandex Maps при старте приложения
preloadYandexMaps();

// Предзагружаем фоновые изображения для виджетов и страниц
preloadBackgroundImages();

// Инициализируем dev-инструменты (только в dev режиме)
initDevTools();

// Устанавливаем начальную высоту мертвой зоны
calculateOffset();

// Блокируем ориентацию экрана через Telegram WebApp API (Bot API 8.0+)
if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp) {
  const tgWebApp = (window as any).Telegram.WebApp;

  // lockOrientation() фиксирует ориентацию Mini App на текущем режиме
  if (tgWebApp.lockOrientation && typeof tgWebApp.lockOrientation === 'function') {
    tgWebApp.lockOrientation();
  }
}

// Атрибуция с сайта link приходит стартовым параметром. Разбираем её до первого
// события, чтобы вход был приписан материалу; заодно отмечаем первое касание,
// если человек пришёл в приложение напрямую, минуя сайт link.
markEntry(getStartParam() ?? new URLSearchParams(window.location.search).get("startapp"));
trackFunnel("app_open");

// Инициализация PostHog
const posthogApiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

if (posthogApiKey) {
  posthog.init(posthogApiKey, {
    api_host: posthogHost,
    defaults: '2025-05-24',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
  });
}


if (!location.hash || location.hash === "#" || !location.hash.startsWith("#/")) {
  location.hash = "#/";
}

const rootElement = document.getElementById("app");

if (!rootElement) {
  throw new Error("Root element '#app' not found");
}

const root = ReactDOM.createRoot(rootElement);

/**
 * Запуск приложения.
 *
 * Вынесен в функцию ради показа механики: если открыт вход за участника
 * презентации, приложение должно дождаться токена и только потом рисовать
 * экраны — иначе первые запросы уйдут без него и человек увидит чужие данные
 * или пустоту. В обычной жизни пропуска нет, и запуск идёт сразу.
 */
function startApp() {
  try {
        root.render(
          <DebugProvider>
            {posthogApiKey ? (
              <PostHogProvider client={posthog}>
                <HashRouter future={{v7_startTransition: true, v7_relativeSplatPath: true,}}>
                  <Suspense fallback={null}>
                    <AppRouter />
                  </Suspense>
                </HashRouter>
                <OrderFeedbackManager />
                <CloseCellModalManager />
                <Toaster richColors theme="system" position="bottom-left" />
              </PostHogProvider>
            ) : (
              <>
                <HashRouter future={{v7_startTransition: true, v7_relativeSplatPath: true,}}>
                  <Suspense fallback={null}>
                    <AppRouter />
                  </Suspense>
                </HashRouter>
                <OrderFeedbackManager />
                <CloseCellModalManager />
                <Toaster richColors theme="system" position="bottom-left" />
              </>
            )}
          </DebugProvider>
        );
  } catch (error) {
    console.error("❌ Error rendering app:", error);
    throw error;
  }
}

if (hasDemoTicket()) {
  applyDemoLoginFromUrl().finally(startApp);
} else {
  startApp();
}
